"use client"

import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from "recharts"
import { GenerativeAvatar } from "@/components/GenerativeAvatar"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { type ChartConfig, ChartContainer } from "@/components/ui/chart"

interface DistributionBarChartProps {
  data: Array<{
    name: string
    value: number
  }>
  height?: number
  color?: string
  showAverageLine?: boolean // Whether to show a dotted average line
  renderAvatars?: boolean // Whether to render avatar images on x-axis
  avatarType?: "contributor" | "team" | "repository" // Type of avatar to render
  getOrganizationName?: (name: string) => string | undefined // For repositories, get organization name
}

// Constants for avatar rendering strategy
const MIN_BAR_WIDTH_FOR_AVATARS = 28 // Minimum bar width to render avatars below bars
const DEFAULT_AVATAR_SIZE = 28 // Default avatar size for hover mode
const MAX_AVATAR_SIZE = 28 // Maximum avatar size (cap at 28px)
const Y_AXIS_RESERVED_WIDTH = 40 // Width reserved for Y-axis
const MAX_BAR_WIDTH = 60 // Maximum width for individual bars (Recharts default)
const BAR_SPACING_RATIO = 0.9 // Recharts barCategoryGap spacing (10% gap)
const BAR_MARGIN = 8 // Additional margin between bars

// Calculate avatar rendering strategy based on bar width
function calculateAvatarStrategy(
  containerWidth: number,
  dataLength: number,
  margins: { left: number; right: number },
): {
  barWidth: number
  renderMode: "always" | "hover" // Always show or show on hover
  avatarSize: number
} {
  if (dataLength === 0) {
    return { barWidth: 0, renderMode: "always", avatarSize: MAX_AVATAR_SIZE }
  }

  // Calculate available width for bars
  const availableWidth = containerWidth - margins.left - margins.right - Y_AXIS_RESERVED_WIDTH

  // Calculate bar width (Recharts uses maxBarSize=60, but actual width depends on spacing)
  // Recharts automatically spaces bars with barCategoryGap (default 10%)
  // So we estimate: available width / count, minus 10% gap, capped at maxBarSize
  const estimatedBarWidth = Math.min(
    MAX_BAR_WIDTH,
    (availableWidth / dataLength) * BAR_SPACING_RATIO - BAR_MARGIN,
  )

  if (estimatedBarWidth >= MIN_BAR_WIDTH_FOR_AVATARS) {
    // Bar is wide enough: render avatar with size capped at MAX_AVATAR_SIZE
    return {
      barWidth: estimatedBarWidth,
      renderMode: "always",
      avatarSize: Math.min(MAX_AVATAR_SIZE, Math.floor(estimatedBarWidth)),
    }
  }

  // Bar is too narrow: show avatar on hover with fixed 28px size
  return {
    barWidth: estimatedBarWidth,
    renderMode: "hover",
    avatarSize: MAX_AVATAR_SIZE,
  }
}

// Helper to safely encode GitHub handles for URLs
function encodeGitHubHandle(handle: string): string {
  return encodeURIComponent(handle.replace(/[^a-zA-Z0-9-]/g, ""))
}

// Helper to get GitHub avatar URL with validation
function getGitHubAvatarUrl(handle: string, size = 32): string {
  const safeHandle = encodeGitHubHandle(handle)
  return `https://github.com/${safeHandle}.png?size=${size}`
}

// Custom tick component that renders avatars for contributors
const ContributorAvatarTick = ({
  x,
  y,
  payload,
  size = 24,
}: {
  x: number
  y: number
  payload: { value: string }
  size?: number
}) => {
  const handle = payload.value
  // Request 2x resolution for high-DPI/retina displays to ensure crisp rendering
  const avatarUrl = getGitHubAvatarUrl(handle, size * 2)
  const fallback = handle.slice(0, 2).toUpperCase()

  const halfSize = size / 2
  const fontSize = Math.max(8, size * 0.4)

  // Generate a consistent color for the fallback based on the handle
  const getColorFromHandle = (str: string): string => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash)
    }
    const hue = Math.abs(hash % 360)
    return `hsl(${hue}, 65%, 50%)`
  }

  return (
    <g transform={`translate(${x},${y})`}>
      {/* SVG fallback - always visible as a baseline */}
      <circle cx={0} cy={halfSize} r={halfSize} fill={getColorFromHandle(handle)} opacity={0.9} />
      <text
        x={0}
        y={halfSize}
        fontSize={fontSize}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontWeight="500"
      >
        {fallback}
      </text>
      {/* Try to load actual avatar image - will overlay if successful */}
      <foreignObject
        x={-halfSize}
        y={0}
        width={size}
        height={size}
        style={{ pointerEvents: "none" }}
      >
        <div
          className="flex items-center justify-center"
          style={{ width: `${size}px`, height: `${size}px` }}
        >
          <Avatar className="shrink-0" style={{ width: `${size}px`, height: `${size}px` }}>
            <AvatarImage src={avatarUrl} alt={handle} />
            <AvatarFallback
              delayMs={0}
              style={{ fontSize: `${fontSize}px`, backgroundColor: "transparent" }}
            >
              {/* Empty fallback since we have SVG fallback below */}
            </AvatarFallback>
          </Avatar>
        </div>
      </foreignObject>
    </g>
  )
}

// Custom tick component for generative avatars (teams/repositories)
const GenerativeAvatarTick = ({
  x,
  y,
  payload,
  type,
  getOrganizationName,
  size = 24,
}: {
  x: number
  y: number
  payload: { value: string }
  type: "team" | "repository"
  getOrganizationName?: (name: string) => string | undefined
  size?: number
}) => {
  const name = payload.value
  const orgName =
    type === "repository" && getOrganizationName ? getOrganizationName(name) : undefined

  const halfSize = size / 2

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-halfSize} y={0} width={size} height={size}>
        <div className="flex items-center justify-center">
          <GenerativeAvatar name={name} type={type} organizationName={orgName} size={size} />
        </div>
      </foreignObject>
    </g>
  )
}

export const DistributionBarChart = observer(
  ({
    data,
    height = 200,
    color = "hsl(var(--primary))",
    showAverageLine = false,
    renderAvatars = false,
    avatarType = "contributor",
    getOrganizationName,
  }: DistributionBarChartProps) => {
    // Ref to measure chart container width
    const containerRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(0)
    // Use a ref to track previous width to avoid stale closure issues
    // (comparing with state value in closure would always use initial 0)
    const previousWidthRef = useRef<number>(0)

    // Measure container width on mount, resize, and when data changes
    // Use ResizeObserver to detect when container becomes visible (e.g., tab switching)
    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth
          // Only update if width has actually changed to avoid unnecessary re-renders
          if (width !== previousWidthRef.current) {
            previousWidthRef.current = width
            setContainerWidth(width)
          }
        }
      }

      // Immediate measurement when effect runs (on mount or data change)
      updateWidth()

      // Use ResizeObserver to detect size changes (including visibility changes)
      const resizeObserver = new ResizeObserver(() => {
        updateWidth()
      })

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }

      // Also listen to window resize as fallback
      window.addEventListener("resize", updateWidth)

      return () => {
        resizeObserver.disconnect()
        window.removeEventListener("resize", updateWidth)
      }
    }, []) // Re-measure when data changes (e.g., switching insights, time ranges)

    // Calculate average value
    const average =
      data.length > 0 ? data.reduce((sum, item) => sum + item.value, 0) / data.length : 0

    // Calculate avatar rendering strategy based on container width and bar width
    // Memoized to ensure stable reference for useCallback dependencies
    const avatarStrategy = useMemo(
      () =>
        renderAvatars
          ? calculateAvatarStrategy(containerWidth, data.length, { left: 10, right: 10 })
          : null,
      [renderAvatars, containerWidth, data.length],
    )

    // Extract stable renderMode for useCallback dependency
    // Use ref for avatarSize to avoid recreating tick component when size changes
    const renderMode = avatarStrategy?.renderMode || "hover"
    const avatarSizeRef = useRef(DEFAULT_AVATAR_SIZE)
    avatarSizeRef.current = avatarStrategy?.avatarSize || DEFAULT_AVATAR_SIZE

    // Add average to each data point for line rendering and index for avatar positioning
    const dataWithAverage = data.map((item, index) => ({
      ...item,
      average: average,
      index: index,
    }))

    const chartConfig: ChartConfig = {
      value: {
        label: "Value",
        color: color,
      },
      average: {
        label: "Average",
        color: "hsl(var(--destructive))",
      },
    }

    // Create a custom tick component that uses bar width for avatar sizing
    // Memoized to prevent unnecessary re-renders and ensure Recharts renders all ticks consistently
    // Only recreates when renderMode changes, not when avatarSize changes (uses ref)
    const ConditionalAvatarTick = useCallback(
      (props: { x: number; y: number; payload: { value: string } } & Record<string, unknown>) => {
        // Recharts passes the tick props
        if (renderMode === "hover") {
          // Don't render avatars below bars if bar width < 28px (show in tooltip instead)
          // Return diagonal text tick for narrow bars with truncation (first 8 chars + "...")
          const label = props.payload.value
          const truncatedLabel = label.length > 8 ? `${label.slice(0, 8)}...` : label

          return (
            <text
              x={props.x}
              y={props.y}
              fontSize="11px"
              textAnchor="end"
              fill="currentColor"
              className="text-muted-foreground"
              transform={`rotate(-45 ${props.x} ${props.y})`}
            >
              {truncatedLabel}
            </text>
          )
        }

        if (avatarType === "contributor") {
          return (
            <ContributorAvatarTick
              x={props.x}
              y={props.y}
              payload={props.payload}
              size={avatarSizeRef.current}
            />
          )
        }

        return (
          <GenerativeAvatarTick
            x={props.x}
            y={props.y}
            payload={props.payload}
            type={avatarType}
            getOrganizationName={getOrganizationName}
            size={avatarSizeRef.current}
          />
        )
      },
      [renderMode, avatarType, getOrganizationName],
    )

    // Calculate appropriate height for x-axis based on avatar rendering
    const xAxisHeight =
      renderAvatars && avatarStrategy ? Math.max(40, avatarStrategy.avatarSize + 16) : 60

    return (
      <div ref={containerRef} className="w-full mb-6">
        <ChartContainer config={chartConfig} className="w-full" style={{ height: `${height}px` }}>
          <ComposedChart
            data={dataWithAverage}
            accessibilityLayer
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="name"
              tickLine={false}
              tickMargin={8}
              axisLine={false}
              interval={0}
              tick={renderAvatars ? ConditionalAvatarTick : { fontSize: "11px" }}
              angle={renderAvatars ? 0 : -45}
              textAnchor={renderAvatars ? "middle" : "end"}
              height={xAxisHeight}
            />
            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: "11px" }} width={40} />
            <Tooltip
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const dataPoint = payload[0].payload
                  const shouldShowAvatarInTooltip =
                    renderAvatars && avatarStrategy && avatarStrategy.renderMode === "hover"

                  return (
                    <div className="rounded-lg border bg-background p-2 shadow-sm">
                      <div className="flex flex-col gap-2">
                        {shouldShowAvatarInTooltip && (
                          <div className="flex items-center justify-center">
                            {avatarType === "contributor" ? (
                              <Avatar style={{ width: "40px", height: "40px" }}>
                                <AvatarImage
                                  src={getGitHubAvatarUrl(dataPoint.name, 80)}
                                  alt={dataPoint.name}
                                />
                                <AvatarFallback style={{ fontSize: "16px" }}>
                                  {dataPoint.name.slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                            ) : (
                              <GenerativeAvatar
                                name={dataPoint.name}
                                type={avatarType}
                                organizationName={
                                  avatarType === "repository" && getOrganizationName
                                    ? getOrganizationName(dataPoint.name)
                                    : undefined
                                }
                                size="lg"
                              />
                            )}
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex flex-col">
                            <span className="text-[0.70rem] uppercase text-muted-foreground">
                              {dataPoint.name}
                            </span>
                            <span className="font-bold text-muted-foreground">
                              {payload[0].value}
                            </span>
                          </div>
                          {showAverageLine && (
                            <div className="flex flex-col">
                              <span className="text-[0.70rem] uppercase text-muted-foreground">
                                Average
                              </span>
                              <span className="font-bold text-muted-foreground">
                                {average.toFixed(1)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                }
                return null
              }}
            />
            <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} maxBarSize={60} />
            {showAverageLine && (
              <Line
                type="monotone"
                dataKey="average"
                stroke="hsl(var(--destructive))"
                strokeWidth={1}
                strokeDasharray="2 2"
                dot={false}
              />
            )}
          </ComposedChart>
        </ChartContainer>
      </div>
    )
  },
)

DistributionBarChart.displayName = "DistributionBarChart"
